import { Controller } from "@hotwired/stimulus";

// Shared global counter for open dialogs (modals and slideovers)
// This ensures proper scrollbar compensation when mixing modals and slideovers
if (!window.__openDialogCount) {
  window.__openDialogCount = 0;
}

export default class extends Controller {
  static targets = ["dialog", "template"];
  static values = {
    alwaysReloadContent: { default: false, type: Boolean }, // Whether to always reload content
    lazyLoad: { default: false, type: Boolean }, // Whether to lazy load the slideover content
    open: { default: false, type: Boolean }, // Whether the slideover is open
    turboFrameSrc: { default: "", type: String }, // URL for the turbo frame
  };

  backdropClose(event) {
    // Only close if clicking directly on the dialog element (the backdrop)
    if (event.target.nodeName === "DIALOG") {
      // Stop propagation to prevent closing parent dialogs
      event.stopPropagation();

      this.close();
    }
  }

  beforeCache() {
    this.close();
  }

  close() {
    // If not open, don't do anything
    if (!this.isOpen) return;

    if (this.hasDialogTarget) {
      this.dialogTarget.setAttribute("closing", "");

      Promise.all(this.dialogTarget.getAnimations().map((animation) => animation.finished)).then(() => {
        this.dialogTarget.removeAttribute("closing");
        this.dialogTarget.close();

        // Decrement the counter and mark this slideover as closed
        window.__openDialogCount--;
        this.isOpen = false;

        // Only remove scrollbar compensation if no dialogs are open
        if (window.__openDialogCount === 0) {
          document.documentElement.style.removeProperty("--scrollbar-compensation");
          document.body.classList.remove("modal-open", "slideover-open");
        }
      });
    }
  }

  connect() {
    if (this.openValue) this.open();
    this.boundBeforeCache = this.beforeCache;
    document.addEventListener("turbo:before-cache", this.boundBeforeCache);
    this.contentLoaded = false;
    this.isOpen = false; // Track if this specific slideover is open

    // Add event listener for when dialog is closed by any means (including Escape key)
    this.dialogTarget.addEventListener("close", this.handleDialogClose.bind(this));
  }

  disconnect() {
    // If this slideover was open when disconnecting, decrement the counter
    if (this.isOpen) {
      window.__openDialogCount--;
      this.isOpen = false;

      // Only remove scrollbar compensation if no dialogs are open
      if (window.__openDialogCount === 0) {
        document.documentElement.style.removeProperty("--scrollbar-compensation");
        document.body.classList.remove("modal-open", "slideover-open");
      }
    }

    document.removeEventListener("turbo:before-cache", this.boundBeforeCache);
    this.dialogTarget.removeEventListener("close", this.handleDialogClose.bind(this));
  }

  // Calculate actual scrollbar width
  getScrollbarWidth() {
    // Create a temporary div to measure scrollbar width
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar"; // Force scrollbars on IE/Edge
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  // Handle cleanup when dialog is closed by any means
  handleDialogClose() {
    // If the slideover was open, decrement the counter
    if (this.isOpen) {
      window.__openDialogCount--;
      this.isOpen = false;

      // Only remove scrollbar compensation if no dialogs are open
      if (window.__openDialogCount === 0) {
        document.documentElement.style.removeProperty("--scrollbar-compensation");
        document.body.classList.remove("modal-open", "slideover-open");
      }
    }

    // Ensure the closing attribute is removed
    this.dialogTarget.removeAttribute("closing");
  }

  hide(event) {
    if (event) event.preventDefault();
    this.close();
  }

  async open() {
    // If already open, don't do anything
    if (this.isOpen) return;

    // If lazy loading is enabled and content hasn't been loaded yet, load it now
    if (this.lazyLoadValue && !this.contentLoaded) {
      await this.#loadTemplateContent();

      if (!this.alwaysReloadContentValue) {
        this.contentLoaded = true;
      }
    }

    // Increment the counter and mark this slideover as open
    window.__openDialogCount++;
    this.isOpen = true;

    // Only apply scrollbar compensation if this is the first dialog
    if (window.__openDialogCount === 1) {
      const scrollbarWidth = this.getScrollbarWidth();
      if (scrollbarWidth > 0) {
        // Set CSS custom property for scrollbar width
        document.documentElement.style.setProperty("--scrollbar-compensation", `${scrollbarWidth}px`);
        // Add class to body to trigger compensation
        document.body.classList.add("slideover-open");
      }
    }

    this.dialogTarget.showModal();
  }

  show() {
    this.open();
  }

  async #loadTemplateContent() {
    // Find the container in the dialog to append content to
    const container = this.dialogTarget.querySelector("[data-slideover-content]") || this.dialogTarget;

    // Check if we should use Turbo Frame lazy loading
    if (this.turboFrameSrcValue) {
      // Look for a turbo-frame in the container
      let turboFrame = container.querySelector("turbo-frame");

      if (!turboFrame) {
        // Create a turbo-frame if it doesn't exist
        turboFrame = document.createElement("turbo-frame");
        turboFrame.id = "slideover-lazy-content";

        // Clear any loading indicators or placeholder content
        container.innerHTML = "";
        container.appendChild(turboFrame);
      }

      // Set the src to trigger the lazy load
      turboFrame.src = this.turboFrameSrcValue;

      // Wait for the turbo-frame to load
      return new Promise((resolve) => {
        const handleLoad = () => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        };

        turboFrame.addEventListener("turbo:frame-load", handleLoad);

        // Fallback timeout in case the frame doesn't load
        setTimeout(() => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        }, 5000);
      });
    } else if (this.hasTemplateTarget) {
      // Use template-based lazy loading
      const templateContent = this.templateTarget.content.cloneNode(true);

      // Clear any loading indicators or placeholder content
      container.innerHTML = "";

      // Append the template content
      container.appendChild(templateContent);
    }
  }
}
